import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { translateError, handleUnauthorizedError } from '@/lib/errorTranslation';
import { LanguageToggle } from '@/components/LanguageToggle';
import { LogOut, Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function DoctorPatients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({
    parent_email: '',
    first_name: '',
    last_name: '',
    dob: '',
    sex: 'not_specified' as 'male' | 'female' | 'not_specified',
    vaccines_up_to_date: false,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const result: any = await api.getPatients();
        setPatients(result.results || result.patients || []);
      } catch (error: any) {
        if (handleUnauthorizedError(error, navigate, 'doctor')) return;
        toast({ title: t('app.error'), description: translateError(error, t), variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast, t]);

  const handleLogout = () => {
    navigate('/logout');
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ parent_email: '', first_name: '', last_name: '', dob: '', sex: 'not_specified', vaccines_up_to_date: false });
    setShowForm(true);
  };

  const openEdit = (p: any) => {
    setEditing(p);
    setForm({
      parent_email: p.parent_email || '',
      first_name: p.first_name || p.name?.split(' ')[0] || '',
      last_name: p.last_name || p.name?.split(' ').slice(1).join(' ') || '',
      dob: p.dob || '',
      sex: p.sex || 'not_specified',
      vaccines_up_to_date: !!p.vaccines_up_to_date,
    });
    setShowForm(true);
  };

  const savePatient = async () => {
    try {
      const payload = {
        parent_email: form.parent_email,
        name: `${form.first_name} ${form.last_name}`.trim(),
        dob: form.dob,
        sex: form.sex,
        vaccines_up_to_date: form.vaccines_up_to_date,
      };
      if (editing) {
        await api.updatePatient(editing.id, payload);
        toast({ title: t('patient.updated') });
      } else {
        await api.createPatient(payload);
        toast({ title: t('patient.created') });
      }
      const result: any = await api.getPatients();
      setPatients(result.results || result.patients || []);
      setShowForm(false);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({ title: t('app.error'), description: translateError(error, t), variant: 'destructive' });
    }
  };

  const deletePatient = async (p: any) => {
    try {
      await api.deletePatient(p.id);
      toast({ title: t('patient.deleted') });
      const result: any = await api.getPatients();
      setPatients(result.results || result.patients || []);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({ title: t('app.error'), description: translateError(error, t), variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">👥 {t('nav.patients')}</h1>
          </div>
          <div className="flex gap-2">
            <LanguageToggle />
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />{t('nav.logout')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        <div className="flex justify-between items-center mb-4">
          {/* <h2 className="text-xl font-semibold">{t('patient.title')}</h2> */}
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4 mr-2" /> {t('patient.add')}
          </Button>
        </div>

        {loading && <p>{t('app.loading')}</p>}
        
        {!loading && patients.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('patient.emptyTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">{t('patient.emptyDescription')}</p>
            </CardContent>
          </Card>
        )}
        
        {!loading && patients.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {patients.map((p) => (
              <Card key={p.id} className="">
                <CardHeader>
                  <CardTitle>{p.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{t('patient.id')}: {p.id}</p>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)}>
                      <Pencil className="h-4 w-4 mr-1" /> {t('common.edit')}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => deletePatient(p)}>
                      <Trash2 className="h-4 w-4 mr-1" /> {t('common.delete')}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/doctor/patients/${p.id}`)}>
                      {t('common.view')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">{editing ? t('patient.edit') : t('patient.add')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first">{t('patient.firstName')}</Label>
                <Input id="first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="last">{t('patient.lastName')}</Label>
                <Input id="last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input id="email" type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="dob">{t('patient.dob')}</Label>
                <Input id="dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="sex">{t('patient.sex')}</Label>
                <Select value={form.sex} onValueChange={(value: 'male' | 'female' | 'not_specified') => setForm({ ...form, sex: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('patient.sexNotSpecified')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('patient.sexMale')}</SelectItem>
                    <SelectItem value="female">{t('patient.sexFemale')}</SelectItem>
                    <SelectItem value="not_specified">{t('patient.sexNotSpecified')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={form.vaccines_up_to_date} onChange={(e) => setForm({ ...form, vaccines_up_to_date: e.target.checked })} />
                  <span>{t('patient.vaccinesUpToDate')}</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
              <Button onClick={savePatient}>{t('common.save')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}